import type { ApolloClient } from '@apollo/client';
import {
  GET_CUSTOM_FORMULA_VALUE,
  GET_FACEBOOK_METRICS_VALUES,
  GET_GOOGLE_ADS_METRICS_DRAFT,
  GET_GOOGLE_ANALYTICS_4_METRICS,
  GET_KIPIFY_METRICS_VALUES,
  GET_SHOPIFY_METRICS_VALUES,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  MetricsByConnectorType,
  type KpiSection,
  type Metric,
  type MetricValuesRow,
  ValueType,
} from 'src/utils/gql/types';
import {
  getCorrectCurrencyFormat,
  getCorrectDurationFormat,
  getCorrectNumberFormat,
  getCorrectPercentageFormat,
} from 'src/utils/stringUtils';
import { parseKpiFiltersConfig } from 'src/pages/KPI/utils/parseKpiFiltersConfig';
import { buildKpiFilters } from 'src/pages/KPI/utils/buildKpiFilters';
import { computeKpiCardTotalFromRows } from 'src/pages/KPI/components/KPICard';

type SavedSelectedMetric = {
  id: string;
  metric: Metric;
  connectorType: ConnectorType | MetricsByConnectorType | string;
  showChart: boolean;
};

type DateRangeLike = {
  from: string;
  to: string;
};

type ConnectorRuntime = {
  shopifyConnectorId?: string;
  facebookConnectorId?: string;
  googleAdsConnectorId?: string;
  googleAnalytics4ConnectorId?: string;
  clientAccountId?: string;
  propertyId?: string;
};

function formatMetricValue(value: number, valueType: ValueType): string {
  switch (valueType) {
    case ValueType.currency:
      return getCorrectCurrencyFormat(value);
    case ValueType.percentage:
      return getCorrectPercentageFormat(value);
    case ValueType.duration:
      return getCorrectDurationFormat(value);
    case ValueType.number:
    default:
      return getCorrectNumberFormat(value);
  }
}

function formatDeltaPercent(current: number, previous: number): string | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  const abs = Math.abs(delta);
  const sign = delta > 0 ? '+' : '';
  return `${sign}${getCorrectNumberFormat(abs)}%`;
}

function formatMetricLine(params: {
  metric: Metric;
  currentValue: number;
  compareValue?: number | null;
}): string {
  const currentText = formatMetricValue(
    params.currentValue,
    params.metric.valueType,
  );
  if (
    params.compareValue === null ||
    params.compareValue === undefined ||
    !Number.isFinite(params.compareValue)
  ) {
    return `  - ${params.metric.label}: ${currentText}`;
  }

  const compareText = formatMetricValue(
    params.compareValue,
    params.metric.valueType,
  );
  const deltaText = formatDeltaPercent(
    params.currentValue,
    params.compareValue,
  );
  return `  - ${params.metric.label}: ${currentText} (compare: ${compareText}${deltaText ? `, delta ${deltaText}` : ''})`;
}

function extractRowsFromQueryResult(
  connectorType: ConnectorType | MetricsByConnectorType | string,
  payload: Record<string, unknown> | undefined,
): MetricValuesRow[] {
  if (!payload) return [];

  if (connectorType === ConnectorType.SHOPIFY) {
    const result = payload.getShopifyMetricValues as
      | { metricRows?: MetricValuesRow[] }
      | undefined;
    const rows = result?.metricRows;
    return Array.isArray(rows) ? rows : [];
  }

  if (connectorType === ConnectorType.FACEBOOK) {
    const result = payload.getFacebookMetricsValues as unknown;
    if (Array.isArray(result)) return result as MetricValuesRow[];
    if (result && typeof result === 'object' && 'rows' in result) {
      const rows = (result as { rows?: MetricValuesRow[] }).rows;
      return Array.isArray(rows) ? rows : [];
    }
    return result ? ([result] as MetricValuesRow[]) : [];
  }

  if (connectorType === ConnectorType.GOOGLE_ADS) {
    const result = payload.getGoogleAdsMetrics as unknown;
    return Array.isArray(result)
      ? (result as MetricValuesRow[])
      : result
        ? ([result] as MetricValuesRow[])
        : [];
  }

  if (connectorType === ConnectorType.GOOGlE_ANALYTICS4) {
    const result = payload.getGoogleAnalytics4Metrics as unknown;
    return Array.isArray(result)
      ? (result as MetricValuesRow[])
      : result
        ? ([result] as MetricValuesRow[])
        : [];
  }

  if (
    connectorType === MetricsByConnectorType.KIPIFY ||
    connectorType === ConnectorType.KIPIFY
  ) {
    const result = payload.getKipifyMetricsValues as unknown;
    return Array.isArray(result)
      ? (result as MetricValuesRow[])
      : result
        ? ([result] as MetricValuesRow[])
        : [];
  }

  if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
    const result = payload.getCustomFormulaValues as unknown;
    return Array.isArray(result)
      ? (result as MetricValuesRow[])
      : result
        ? ([result] as MetricValuesRow[])
        : [];
  }

  return [];
}

async function queryMetricRows(params: {
  client: ApolloClient<object>;
  runtime: ConnectorRuntime;
  metric: Metric;
  connectorType: ConnectorType | MetricsByConnectorType | string;
  dateRange: DateRangeLike;
  filters: ReturnType<typeof buildKpiFilters>;
}): Promise<MetricValuesRow[]> {
  const { client, runtime, metric, connectorType, dateRange, filters } = params;

  if (connectorType === ConnectorType.SHOPIFY) {
    if (!runtime.shopifyConnectorId) return [];
    const { data } = await client.query({
      query: GET_SHOPIFY_METRICS_VALUES,
      variables: {
        args: {
          metricKeys: [metric.key],
          splitsKeys: [],
          filters,
          dateRange,
        },
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(connectorType, data);
  }

  if (connectorType === ConnectorType.FACEBOOK) {
    if (!runtime.facebookConnectorId) return [];
    const { data } = await client.query({
      query: GET_FACEBOOK_METRICS_VALUES,
      variables: {
        args: {
          metricKeys: [metric.key],
          splitsKeys: [],
          filters: [],
          dateRange,
        },
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(connectorType, data);
  }

  if (connectorType === ConnectorType.GOOGLE_ADS) {
    if (!runtime.googleAdsConnectorId || !runtime.clientAccountId) return [];
    const { data } = await client.query({
      query: GET_GOOGLE_ADS_METRICS_DRAFT,
      variables: {
        args: {
          metricKeys: [metric.key],
          splitsKeys: [],
          filters: [],
          dateRange,
        },
        clientAccountId: runtime.clientAccountId,
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(connectorType, data);
  }

  if (connectorType === ConnectorType.GOOGlE_ANALYTICS4) {
    if (!runtime.googleAnalytics4ConnectorId || !runtime.propertyId) return [];
    const { data } = await client.query({
      query: GET_GOOGLE_ANALYTICS_4_METRICS,
      variables: {
        args: {
          metricKeys: [metric.key],
          splitsKeys: [],
          filters: [],
          dateRange,
        },
        propertyId: runtime.propertyId,
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(connectorType, data);
  }

  if (
    connectorType === MetricsByConnectorType.KIPIFY ||
    connectorType === ConnectorType.KIPIFY
  ) {
    const { data } = await client.query({
      query: GET_KIPIFY_METRICS_VALUES,
      variables: {
        args: {
          metricKeys: [metric.key],
          splitsKeys: [],
          filters: metric.key === 'markup' ? filters : [],
          dateRange,
        },
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(MetricsByConnectorType.KIPIFY, data);
  }

  if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
    const { data } = await client.query({
      query: GET_CUSTOM_FORMULA_VALUE,
      variables: {
        args: {
          formulaIds: [metric.key],
          splitsKeys: [],
          filters: [],
          dateRange,
        },
      },
      fetchPolicy: 'network-only',
    });
    return extractRowsFromQueryResult(connectorType, data);
  }

  return [];
}

function parseDateRange(value: unknown): DateRangeLike | null {
  if (!value || typeof value !== 'object') return null;
  if (!('from' in value) || !('to' in value)) return null;
  const from = typeof value.from === 'string' ? value.from : '';
  const to = typeof value.to === 'string' ? value.to : '';
  if (!from || !to) return null;
  return { from, to };
}

function parseSelectedMetrics(value: unknown): SavedSelectedMetric[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is SavedSelectedMetric => {
    if (!item || typeof item !== 'object') return false;
    const record = item as SavedSelectedMetric;
    return Boolean(
      record.metric &&
        typeof record.metric === 'object' &&
        typeof record.metric.key === 'string' &&
        typeof record.metric.label === 'string' &&
        typeof record.connectorType === 'string',
    );
  });
}

export async function buildKpiSectionPromptBlocks(params: {
  client: ApolloClient<object>;
  runtime: ConnectorRuntime;
  sections: KpiSection[];
}): Promise<string[]> {
  const { client, runtime, sections } = params;

  return Promise.all(
    sections.map(async (section) => {
      const cfg =
        section.config && typeof section.config === 'object'
          ? (section.config as Record<string, unknown>)
          : {};

      const metrics = parseSelectedMetrics(cfg.selectedMetrics);
      const dateRange = parseDateRange(cfg.dateRange);
      const compareDateRange = parseDateRange(cfg.compareDateRange);
      const savedFilters = parseKpiFiltersConfig(cfg.kpiFilters as never);
      const filters = buildKpiFilters(savedFilters);

      const lines: string[] = [
        `KPI section: "${section.name || 'Untitled section'}"`,
      ];

      if (metrics.length > 0) {
        lines.push(
          `  Metrics: ${metrics.map((item) => item.metric.label).join(', ')}`,
        );
      } else {
        lines.push('  Metrics: (none listed in saved config)');
      }

      if (dateRange) {
        lines.push(`  Date range (saved): ${dateRange.from} → ${dateRange.to}`);
      }
      if (typeof cfg.dateRangePreset === 'string' && cfg.dateRangePreset) {
        lines.push(`  Date preset: ${cfg.dateRangePreset}`);
      }

      const filterBits: string[] = [];
      if (savedFilters.countries.length)
        filterBits.push(`countries: ${savedFilters.countries.join(', ')}`);
      if (savedFilters.customerType)
        filterBits.push(`customer type: ${savedFilters.customerType}`);
      if (savedFilters.tags.length)
        filterBits.push(`tags: ${savedFilters.tags.join(', ')}`);
      if (savedFilters.collections.length)
        filterBits.push(`collections: ${savedFilters.collections.join(', ')}`);
      if (savedFilters.vendors.length)
        filterBits.push(`vendors: ${savedFilters.vendors.join(', ')}`);
      if (savedFilters.productTypes.length)
        filterBits.push(
          `product types: ${savedFilters.productTypes.join(', ')}`,
        );
      if (filterBits.length > 0) {
        lines.push(`  Filters: ${filterBits.join('; ')}`);
      }

      if (!dateRange || metrics.length === 0) {
        lines.push('  Live KPI snapshot: unavailable from saved config.');
        return lines.join('\n');
      }

      const metricLines = await Promise.all(
        metrics.map(async (selectedMetric) => {
          try {
            const currentRows = await queryMetricRows({
              client,
              runtime,
              metric: selectedMetric.metric,
              connectorType: selectedMetric.connectorType,
              dateRange,
              filters,
            });

            if (currentRows.length === 0) {
              return `  - ${selectedMetric.metric.label}: unavailable`;
            }

            const currentValue = computeKpiCardTotalFromRows({
              rows: currentRows,
              connectorType: selectedMetric.connectorType,
              metricKey: selectedMetric.metric.key,
            });

            let compareValue: number | null = null;
            if (compareDateRange) {
              const compareRows = await queryMetricRows({
                client,
                runtime,
                metric: selectedMetric.metric,
                connectorType: selectedMetric.connectorType,
                dateRange: compareDateRange,
                filters,
              });
              if (compareRows.length > 0) {
                compareValue = computeKpiCardTotalFromRows({
                  rows: compareRows,
                  connectorType: selectedMetric.connectorType,
                  metricKey: selectedMetric.metric.key,
                });
              }
            }

            return formatMetricLine({
              metric: selectedMetric.metric,
              currentValue,
              compareValue,
            });
          } catch {
            return `  - ${selectedMetric.metric.label}: unavailable`;
          }
        }),
      );

      if (
        typeof cfg.compareMode === 'string' &&
        cfg.compareMode &&
        cfg.compareMode !== 'none'
      ) {
        lines.push(`  Compare mode: ${cfg.compareMode}`);
        if (compareDateRange) {
          lines.push(
            `  Compare range: ${compareDateRange.from} → ${compareDateRange.to}`,
          );
        }
      }

      lines.push('  Live KPI snapshot:');
      lines.push(...metricLines);

      return lines.join('\n');
    }),
  );
}
